<!-- @format -->

<template>
    <div class="gst-event-totals-container u-width-100">
        <div class="d-flex">
            <div class="price-container d-flex flex-column flex-grow-1">
                <span class="total-title">{{ title }}:</span>
                <span
                    v-if="totalPrice"
                    class="total-subtitle mt-1">
                    {{ selectedItemsWithQuantity }}
                </span>
            </div>
            <div
                v-if="totalPrice"
                class="price-container d-flex flex-column flex-grow-1 text-right">
                <div class="total-title">
                    <span>{{ totalPrice | currencyFilter(currency) }}</span>
                </div>
                <div class="total-subtitle mt-1">
                    <span>{{ pricePerPerson | currencyFilter(currency) }}</span>
                    <span>
                        {{ $t('_common:terms.perPerson') }}
                    </span>
                </div>
            </div>
            <div
                v-else
                class="d-flex flex-column flex-grow-1 text-right gst-event-totals-container__no-price">
                <h4>{{ noSelectionPrice }}</h4>
            </div>
        </div>
        <EventPriceBreakdown
            v-if="!hidePriceBreakdown && ticketOffer"
            class="price-container__breakdown mt-1"
            :base-price="totalBasePrice"
            :fee-price="ticketFeesAndTaxes"
            :currency="ticketOffer.currency"
            :all-inclusive-pricing="ticketOffer.allInclusivePricing"
            :hotel-price="hotelPrice"
            :processing-fee="processingFee" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventPriceBreakdown from './EventTotalsContainer/EventPriceBreakdown.vue';

export default {
    name: 'EventTotalsContainer',
    components: {
        EventPriceBreakdown,
    },
    props: {
        title: {
            type: String,
            default: null,
        },
        bundleProducts: {
            type: Object,
            default: null,
        },
        event: {
            type: Object,
            required: true,
        },
        hidePriceBreakdown: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters({
            ticketFeesAndTaxes: 'orderSelection/getTicketFeesAndTaxes',
        }),
        selectedHotel() {
            if (this.bundleProducts.hasHotelReservation()) {
                return this.bundleProducts.hotelReservation.hotel;
            }
            return null;
        },
        currency() {
            if (this.selectedHotel) {
                return this.selectedHotel.selectedRoom
                    ? this.selectedHotel.selectedRoom.selectedRate.currency
                    : this.event.currency;
            }

            return this.event.currency;
        },
        adultsCount() {
            return (
                (this.selectedHotel &&
                    this.selectedHotel.selectedRoom.accommodation.guests.adultsCount) ||
                0
            );
        },
        ticketsQuantity() {
            const { tickets } = this.bundleProducts.getQuantity();

            return tickets;
        },
        ticketOffer() {
            if (this.bundleProducts.eventTicket) {
                const offer = this.bundleProducts.eventTicket.ticket?.offers[0];

                return offer;
            }
            return null;
        },
        totalBasePrice() {
            return this.bundleProducts.eventTicket.totals.basePrice;
        },
        processingFee() {
            return this.bundleProducts.getProcessingFee();
        },
        hotelPrice() {
            if (this.bundleProducts.hasHotelReservation()) {
                return this.bundleProducts.hotelReservation.totals.displayPrice;
            }
            return 0;
        },
        totalPrice() {
            const totals = this.bundleProducts.getTotals();
            return totals.totalPrice;
        },
        pricePerPerson() {
            return this.totalPrice / (this.adultsCount || this.ticketsQuantity);
        },
        noSelectionPrice() {
            const tempPrice = this.$options.filters.currencyFilter(0, this.currency);
            const price = tempPrice.replace('0.00', '-');

            return price;
        },
        selectedItemsWithQuantity() {
            let items = [];

            if (this.bundleProducts.hasHotelReservation()) {
                items.push(
                    this.$t('_common:terms.nightWithCount', {
                        count: this.selectedHotel.selectedRoom.accommodation.nights,
                    })
                );
            }

            if (this.ticketsQuantity) {
                items.unshift(
                    this.$t('_common:terms.ticketWithCount', { count: this.ticketsQuantity })
                );
            }

            return items.join(' + ');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-event-totals-container {
    padding: theme-spacing(4);
    background: theme-color-hex('quinary');
    color: theme-color('primary');
    z-index: 1;

    .price-container__breakdown {
        font-size: font-size('xxs');
        font-weight: font-weight('regular');
    }

    h4 {
        line-height: line-height('xxxxl');
        font-size: font-size('l');
        font-weight: font-weight('bold');
    }

    p {
        line-height: line-height('m');
        font-size: font-size('xxs');
        font-weight: font-weight('regular');
    }

    .gst-event-totals-container__no-price {
        min-height: 39px;
    }

    .price-container {
        line-height: line-height('m');
        color: theme-color('anchor');

        .total-title {
            font-size: font-size('xxl');
            font-weight: font-weight('bold');
        }

        .total-subtitle {
            font-size: font-size('xxs');
            font-weight: font-weight('large');
        }
    }
}
</style>
